import { JEPX_PRICE_CONSTRAINT } from '~/models/numberInputConstraint'

/**
 * 作成・編集モーダルが手数料自動考慮をどう扱うか (AC-BSP-004b)。
 *
 * - `'loading'` / `'unavailable'`: 入札制限 (ON/OFF の設定を含む) の取得中・取得失敗。 ON か OFF かが
 *   決まらず、 手数料を加減算しない価格で発注してしまう恐れがあるので、 保存だけを止める。
 * - `'off'`: 設定が OFF。 入力値をそのまま入札価格にする。
 * - `'fallbackOff'`: 設定は ON だが手数料単価を使えない間 (ロード中・取得失敗・JEPX 会員 ID 未設定)。
 *   OFF として扱い、 作成・編集は止めない (10:00 の入札締切までに作成できなくなるのを避ける)。
 * - `'on'`: 設定が ON で手数料単価を使える。 入力値は希望価格。
 */
export type SpotFeeAutoConsider =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'off' }
  | { kind: 'fallbackOff' }
  | { kind: 'on'; feeYenPerKwh: number }

export const resolveSpotFeeAutoConsider = ({
  bidLimitsLoading,
  bidLimitsError,
  feeAutoConsiderEnabled,
  feeYenPerKwh,
}: {
  bidLimitsLoading: boolean
  bidLimitsError: boolean
  feeAutoConsiderEnabled: boolean
  feeYenPerKwh: number | undefined
}): SpotFeeAutoConsider => {
  if (bidLimitsLoading) return { kind: 'loading' }
  if (bidLimitsError) return { kind: 'unavailable' }
  if (!feeAutoConsiderEnabled) return { kind: 'off' }
  if (feeYenPerKwh === undefined) return { kind: 'fallbackOff' }
  return { kind: 'on', feeYenPerKwh }
}

/** ON か OFF かが決まっておらず、 保存を止める間。 */
export const isSpotFeeAutoConsiderPending = (
  fee: SpotFeeAutoConsider,
): boolean => fee.kind === 'loading' || fee.kind === 'unavailable'

/**
 * 価格入力欄の制約 (min / max / step)。 ON でない間は、 AC-BSP-050 のとおり入札価格の値域 (`JEPX_PRICE_CONSTRAINT`)
 * のまま。 ON の欄は希望価格で、 新規作成・保存済みの札を開く経路 (編集・複製・再入札) を問わず、 希望価格そのものの
 * 範囲では止めない (#11466 Q117 = B: 売り 0.01・買い 999 の札を ON でも作れる)。 欄は値域の両端を手数料単価ぶん
 * 広げる (どの売買でも値域を外れる 999 + 単価 超の打鍵は、 AC-BSP-050 と同じく受け付けない)。
 *
 * 単価を使う算術を画面に残すのは、 AC-BSP-004b と AC-BSP-050 がこの範囲を入力欄の打鍵の上限として規定するため。
 * 算出した入札価格を受け付けるかの判定は backend のプレビューが持ち、 ここは事前の入力制約に留める (範囲を
 * プレビュー応答に載せる案は、 単価から一意に決まる値のために contract を変えるので採らない)。
 */
export const priceInputConstraint = (
  fee: SpotFeeAutoConsider,
): { readonly min: number; readonly max: number; readonly step: number } => {
  if (fee.kind !== 'on') return JEPX_PRICE_CONSTRAINT
  const feeCents = Math.round(fee.feeYenPerKwh * 100)
  return {
    min: (Math.round(JEPX_PRICE_CONSTRAINT.min * 100) - feeCents) / 100,
    max: (Math.round(JEPX_PRICE_CONSTRAINT.max * 100) + feeCents) / 100,
    step: JEPX_PRICE_CONSTRAINT.step,
  }
}
